import { z } from 'zod';
import { coercedBoolean } from './helpers';
import { v1ScopeSchema } from './v1/scopes';
/**
 * Schemas for organization management API keys (KIR-3746).
 *
 * A management key is a **Descope Access Key**. Descope owns authentication,
 * tenant scoping, the role snapshot, and expiry; the local `api_keys` row is a
 * display mirror only (`key_type = 'management'`) whose `keyHash` / `keyPrefix`
 * are sentinels that are never authenticated against.
 *
 * The plaintext access key is returned exactly once, by `create`. Nothing else
 * on this surface can ever hand it back.
 */
export const managementApiKeySchema = z.object({
    /** Local mirror row id — the id used to revoke. */
    id: z.string(),
    /** Descope Access Key id — the source of truth this row mirrors. */
    descopeAccessKeyId: z.string(),
    /** First 10 characters of the Descope access-key id. Display only. */
    keyPrefix: z.string(),
    name: z.string().nullable(),
    createdAt: z.coerce.date(),
    expiresAt: z.coerce.date().nullable(),
    lastUsedAt: z.coerce.date().nullable(),
    isActive: z.boolean(),
    /**
     * The key's access scopes (KIR-4377), or `null` for a key minted before
     * scopes existed — which means unrestricted, and renders as *Full access*.
     *
     * Chosen at mint time and immutable: widening means minting a new key,
     * narrowing means revoking and re-minting. A key's plaintext is shown exactly
     * once, so what a holder was handed is what it can do, for its whole life.
     */
    scopes: z.array(v1ScopeSchema).nullable(),
    /** Local user id of whoever minted the key. */
    createdByUserId: z.string().nullable(),
    /** Display name of the creator, joined from `users`. */
    createdByName: z.string().nullable(),
    createdByEmail: z.string().nullable(),
});
export const managementApiKeyListSchema = z.array(managementApiKeySchema);
/**
 * Revoked keys are hidden by default so the Settings list shows live keys only.
 */
export const listManagementApiKeysQuerySchema = z.object({
    includeRevoked: coercedBoolean().optional().default(false),
});
export const createManagementApiKeyRequestSchema = z.object({
    name: z.string().trim().min(1).max(100),
    /**
     * The scopes to grant (KIR-4377). Required and non-empty: a key with no
     * scopes could reach nothing but the health check, so an omitted list is far
     * more likely to be a caller that has not been updated than a deliberate
     * choice — and defaulting it would have to pick between "nothing", which
     * silently mints a useless key, and "everything", which silently mints the
     * most powerful one.
     *
     * Duplicates are collapsed rather than rejected: a UI that sends
     * `alerts:read` twice has expressed one unambiguous intent, and 400ing on it
     * would be pedantry the caller cannot act on.
     */
    scopes: z
        .array(v1ScopeSchema)
        .min(1)
        .transform(scopes => [...new Set(scopes)]),
    /**
     * Optional absolute expiry. Omitted means the key never expires.
     *
     * Must be in the future: Descope accepts a past `expireTime` and mints a key
     * that is already dead, which would return 201 with a one-time plaintext the
     * caller can never use, plus a mirror row claiming to be active.
     */
    expiresAt: z.coerce
        .date()
        .refine(value => value.getTime() > Date.now(), {
        message: 'expiresAt must be in the future',
    })
        .optional(),
});
/**
 * The mirror row plus the plaintext Descope access key. `accessKey` is present
 * on this response and nowhere else — it is never stored and never re-readable.
 */
export const createManagementApiKeyResponseSchema = managementApiKeySchema.extend({
    accessKey: z.string(),
});
export const revokeManagementApiKeyResponseSchema = z.object({
    id: z.string(),
});
//# sourceMappingURL=management-api-keys.js.map